import { ThemeManager } from '../react/themes/theme-manager';
import { FeatureFlagsManager } from '../utils/core/feature-flags-manager';
import { permissionChecks } from '../utils/permission-check';
import { ProjectGeodataManager } from './project-geodata-manager';

export type ViewingMode = 'classic' | 'flexible';
export type FlexibleCameraType = 'perspective' | 'orthographic';
export type ViewLayer = 'orthophoto' | 'tiledModel' | 'pointCloud';
/** What the classic viewing mode shows: one layer, with the camera it is viewed with. */
export type ClassicView = ViewLayer;
export type OrthophotoBlending = 'smooth' | 'pixelated';
/**
 * How the user controls the perspective camera: orbiting around a pivot point, or flying freely in
 * first person.
 */
export type CameraControl = 'orbit' | 'fly';

export type OrthophotoSettings = {
  blending: OrthophotoBlending;
  /** 0 to 1. */
  opacity: number;
  /** Height in the project's vertical datum. Only used by the perspective camera in flexible mode. */
  altitude: number;
};

export type ViewSettingsSnapshot = {
  /**
   * Whether SkyView is the CAD viewer: a project without timeline data, showing only the assets.
   * Set when SkyView initializes, whatever the viewing mode.
   */
  cadViewer: boolean;
  viewingMode: ViewingMode;
  classicView: ClassicView;
  camera: FlexibleCameraType;
  /** The chosen camera control, see ViewSettings.cameraControl for the one in use. */
  cameraControl: CameraControl;
  layers: Record<ViewLayer, boolean>;
  /** 0 to 1. The orthophoto's is in its own settings. */
  opacity: Record<Exclude<ViewLayer, 'orthophoto'>, number>;
  orthophoto: OrthophotoSettings;
};

const viewingModeStorageKey = 'skyview-viewing-mode';

function readStoredViewingMode(): ViewingMode {
  try {
    return localStorage.getItem(viewingModeStorageKey) === 'flexible' ? 'flexible' : 'classic';
  } catch {
    return 'classic';
  }
}

function storeViewingMode(mode: ViewingMode) {
  try {
    localStorage.setItem(viewingModeStorageKey, mode);
  } catch {
    // The mode is then only kept for this session
  }
}

const cameraControlStorageKey = 'skyview-camera-control';
const cameraControls: CameraControl[] = ['orbit', 'fly'];

function readStoredCameraControl(): CameraControl {
  try {
    const stored = localStorage.getItem(cameraControlStorageKey);
    return cameraControls.find((control) => control === stored) ?? 'orbit';
  } catch {
    return 'orbit';
  }
}

function storeCameraControl(control: CameraControl) {
  try {
    localStorage.setItem(cameraControlStorageKey, control);
  } catch {
    // The control is then only kept for this session
  }
}

/** What SkyView shows when it opens, before the scene shows its default layer. */
function createDefaultView(): Omit<
  ViewSettingsSnapshot,
  'cadViewer' | 'viewingMode' | 'cameraControl'
> {
  return {
    classicView: 'orthophoto',
    camera: 'orthographic',
    // None until the SkyView scene shows its default, see showDefaultLayer
    layers: { orthophoto: false, tiledModel: false, pointCloud: false },
    opacity: { tiledModel: 1, pointCloud: 1 },
    orthophoto: { blending: 'smooth', opacity: 1, altitude: 0 },
  };
}

/**
 * The view settings from the toolbar's settings cog. Viewer code reads the snapshot and listens
 * for changes, React reads it through useViewSettings.
 */
export class ViewSettings {
  private static _instance: ViewSettings | undefined;

  static get instance() {
    if (!ViewSettings._instance) {
      ViewSettings._instance = new ViewSettings();
    }
    return ViewSettings._instance;
  }

  private snapshot: ViewSettingsSnapshot;
  private readonly listeners = new Set<() => void>();

  private constructor() {
    this.snapshot = {
      cadViewer: false,
      viewingMode: readStoredViewingMode(),
      cameraControl: readStoredCameraControl(),
      ...createDefaultView(),
    };
  }

  get isEnabled() {
    return FeatureFlagsManager.instance.isEnabled('SK-7549-view-settings');
  }

  /** Can the user choose the viewing mode? Shared views open in the scene they were saved with. */
  get canChooseViewingMode() {
    return this.isEnabled && !ThemeManager.instance.isSharedView;
  }

  /** The chosen viewing mode, or classic where it can't be chosen. */
  get viewingMode(): ViewingMode {
    return this.canChooseViewingMode ? this.snapshot.viewingMode : 'classic';
  }

  get isFlexible() {
    return this.viewingMode === 'flexible';
  }

  get isCadViewer() {
    return this.snapshot.cadViewer;
  }

  /**
   * Whether the views are the cameras, in place of the classic views of the layers: in the CAD
   * viewer and the flexible viewing mode. They are by the orientation cube, or in the timeline bar.
   */
  get showsCameras() {
    return this.isCadViewer || this.isFlexible;
  }

  /**
   * Whether the timeline bar (SK-7562-timeline-bar) has the views, with the orientation cube and the
   * camera control in either viewing mode: the classic views are in the SkyView scene too with the
   * view settings. Only where it shows a timeline, as useTimelineBarTimeline, or a chosen flight
   * would go on in the classic views with nothing to stop it.
   */
  get hasViewsInTimelineBar() {
    return (
      this.isEnabled &&
      FeatureFlagsManager.instance.isEnabled('SK-7562-timeline-bar') &&
      permissionChecks.hasAccessToFeature('feature:viewer:toolbar:timeline') &&
      !!ProjectGeodataManager.instance().getActiveTimelineContainer()
    );
  }

  /** Can the user choose the camera control? By the orientation cube, or in the timeline bar. */
  get canChooseCameraControl() {
    return this.showsCameras || this.hasViewsInTimelineBar;
  }

  /** The chosen camera control, or orbit where it can't be chosen. */
  get cameraControl(): CameraControl {
    return this.canChooseCameraControl ? this.snapshot.cameraControl : 'orbit';
  }

  get classicView() {
    return this.snapshot.classicView;
  }

  get camera() {
    return this.snapshot.camera;
  }

  get layers() {
    return this.snapshot.layers;
  }

  get opacity() {
    return this.snapshot.opacity;
  }

  get orthophoto() {
    return this.snapshot.orthophoto;
  }

  getSnapshot = (): ViewSettingsSnapshot => this.snapshot;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  setCadViewer(cadViewer: boolean) {
    if (cadViewer !== this.snapshot.cadViewer) {
      this.update({ cadViewer });
    }
  }

  /**
   * Shows what SkyView shows when it opens, keeping only the viewing mode and the camera control,
   * which are remembered: the layers, camera and display settings were the last SkyView's.
   */
  resetView() {
    this.update(createDefaultView());
  }

  setViewingMode(viewingMode: ViewingMode) {
    storeViewingMode(viewingMode);
    this.update({ viewingMode });
  }

  setCamera(camera: FlexibleCameraType) {
    this.update({ camera });
  }

  setCameraControl(cameraControl: CameraControl) {
    storeCameraControl(cameraControl);
    this.update({ cameraControl });
  }

  setLayerVisible(layer: ViewLayer, visible: boolean) {
    this.update({ layers: { ...this.snapshot.layers, [layer]: visible } });
  }

  /** Shows only these layers, with this camera. */
  showOnlyLayers(layers: ViewLayer[], camera: FlexibleCameraType) {
    this.update({
      camera,
      layers: {
        orthophoto: layers.includes('orthophoto'),
        tiledModel: layers.includes('tiledModel'),
        pointCloud: layers.includes('pointCloud'),
      },
    });
  }

  /** Shows the classic view: only its layer, the orthophoto from above and the others in perspective. */
  showClassicView(classicView: ClassicView) {
    this.update({
      classicView,
      camera: classicView === 'orthophoto' ? 'orthographic' : 'perspective',
      layers: {
        orthophoto: classicView === 'orthophoto',
        tiledModel: classicView === 'tiledModel',
        pointCloud: classicView === 'pointCloud',
      },
    });
  }

  /**
   * Shows the default layer when none of the shown layers is available, such as the first time or
   * in a timeline container without them: the orthophoto, otherwise the tiled model, otherwise the
   * point cloud.
   */
  showDefaultLayer(available: Record<ViewLayer, boolean>) {
    const layers: ViewLayer[] = ['orthophoto', 'tiledModel', 'pointCloud'];
    if (layers.some((layer) => this.snapshot.layers[layer] && available[layer])) {
      return;
    }

    const layer = layers.find((l) => available[l]);
    if (layer) {
      this.showOnlyLayers([layer], layer === 'orthophoto' ? 'orthographic' : 'perspective');
    }
  }

  setOpacity(layer: Exclude<ViewLayer, 'orthophoto'>, opacity: number) {
    this.update({ opacity: { ...this.snapshot.opacity, [layer]: opacity } });
  }

  setOrthophoto(settings: Partial<OrthophotoSettings>) {
    this.update({ orthophoto: { ...this.snapshot.orthophoto, ...settings } });
  }

  private update(changes: Partial<ViewSettingsSnapshot>) {
    this.snapshot = { ...this.snapshot, ...changes };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
